import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useCatalogs, useDestinationDetail, useSources } from '../../api/hooks';
import { ApiError } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import { membershipHasPermission } from '../../auth/permissions';
import { NotFoundPage } from '../ui/NotFoundPage';
import { darkTheme, FONT, FONT_MONO } from '../../theme/tokens';
import {
  adapterKindLabel,
  canToggle,
  type CatalogNames,
  categoryLabel,
  describeCoverage,
  describeFields,
  destinationStatus,
  entityScopeLabel,
  kindLabel,
  sourceStatus,
} from './connectionModel';
import {
  BackToConnections,
  CredentialSetting,
  DetailHeader,
  Notice,
  Setting,
  SettingsList,
} from './connectionUi';
import { KillSwitch } from './KillSwitch';

const t = darkTheme;

/**
 * `/connections/:direction/:id` (SC-3122) — one connection instance's settings.
 * Outbound carries the kill switch; inbound is read-only (sources have no write
 * API yet). Tier 2 editing (field map, catalog scope) and credential entry
 * (write-only secrets) land here later.
 */
export function ConnectionDetail() {
  const { direction, id = '' } = useParams();
  const body =
    direction === 'outbound' ? (
      <DestinationView id={id} />
    ) : direction === 'inbound' ? (
      <SourceView id={id} />
    ) : null;
  if (!body) return <NotFoundPage />;
  return (
    <div style={{ padding: '28px 32px', maxWidth: 820, fontFamily: FONT }}>
      <BackToConnections />
      {body}
    </div>
  );
}

function useCatalogNames(): CatalogNames {
  const catalogs = useCatalogs();
  const names = useMemo(
    () => new Map((catalogs.data ?? []).map((c) => [c.id, c.name])),
    [catalogs.data],
  );
  if (catalogs.data) return names;
  return catalogs.isError ? 'error' : 'loading';
}

function NoAccess() {
  return (
    <Notice>
      You don’t have permission to view this connection’s settings. Ask an organization admin.
    </Notice>
  );
}

function DestinationView({ id }: { id: string }) {
  const { membership } = useAuth();
  const canManage = membershipHasPermission(membership, 'manage_destinations');
  const query = useDestinationDetail(id, canManage);
  const catalogNames = useCatalogNames();

  if (!canManage) return <NoAccess />;
  if (query.isPending) return <Notice>Loading…</Notice>;
  if (query.isError) {
    return (
      <Notice tone="error">
        {query.error instanceof ApiError && query.error.status === 404
          ? 'This connection doesn’t exist, or it has been removed.'
          : 'Couldn’t load this connection.'}
      </Notice>
    );
  }

  const d = query.data;
  const kind = kindLabel(d.kind);
  const coverage = describeCoverage(d.catalog_ids, catalogNames);
  const removing = d.lifecycle === 'deleting';
  const toggleable = canToggle(d);

  return (
    <>
      <DetailHeader
        name={d.name}
        subtitle={`Outbound · ${kind.label}`}
        status={destinationStatus(d)}
        action={toggleable ? <KillSwitch id={d.id} name={d.name} enabled={d.enabled} /> : undefined}
      />
      {removing && (
        <div style={{ marginBottom: 16 }}>
          <Notice tone="error">
            This connection is being removed. Nothing more will be sent to it, and it can’t be
            switched back on.
          </Notice>
        </div>
      )}
      <SettingsList>
        <Setting label="Connection">
          <code style={{ fontFamily: FONT_MONO, fontSize: 12 }}>{d.provider}</code>
        </Setting>
        <Setting label="Role" hint={kind.hint}>
          {kind.label}
        </Setting>
        <Setting label="Catalogs" hint={coverage.hint} tone={coverage.tone === 'none' ? 'warn' : 'plain'}>
          {coverage.label}
        </Setting>
        <Setting label="Products">{entityScopeLabel(d.entity_scope)}</Setting>
        <Setting label="Fields">{describeFields(d.allowed_fields, d.denied_fields)}</Setting>
        <CredentialSetting name={d.credentials_ref} />
      </SettingsList>
    </>
  );
}

function SourceView({ id }: { id: string }) {
  const { membership } = useAuth();
  const canView = membershipHasPermission(membership, 'view_sources');
  // The list, not `GET /sources/{id}`: it is what the table already cached, and an
  // org has a handful of sources.
  const sources = useSources(canView);
  const catalogNames = useCatalogNames();

  if (!canView) return <NoAccess />;
  if (sources.isPending) return <Notice>Loading…</Notice>;
  if (sources.isError) return <Notice tone="error">Couldn’t load this connection.</Notice>;

  const s = sources.data.find((x) => x.id === id);
  if (!s) return <Notice tone="error">This connection doesn’t exist, or it has been removed.</Notice>;

  const category = categoryLabel(s.category);
  const coverage = describeCoverage(s.catalogs, catalogNames);

  return (
    <>
      <DetailHeader
        name={s.name}
        subtitle={`Inbound · ${category.label}`}
        status={sourceStatus(s)}
      />
      <SettingsList>
        <Setting label="Connection">
          <code style={{ fontFamily: FONT_MONO, fontSize: 12 }}>{s.adapter_key}</code>
        </Setting>
        <Setting label="Category" hint={category.hint}>
          {category.label}
        </Setting>
        <Setting
          label="Trust weight"
          hint="How much EKOM believes this source when sources disagree, from 0 to 1."
        >
          {s.weight.toFixed(2)}
        </Setting>
        <Setting
          label="Type"
          tone={s.adapter_kind ? 'plain' : 'warn'}
          hint={s.adapter_kind ? undefined : 'This connection isn’t available in this version of EKOM, so nothing is read from it.'}
        >
          {s.adapter_kind ? adapterKindLabel(s.adapter_kind) : 'Unavailable'}
        </Setting>
        <Setting label="Catalogs" hint={coverage.hint} tone={coverage.tone === 'none' ? 'warn' : 'plain'}>
          {coverage.label}
        </Setting>
        <CredentialSetting name={s.credentials_ref} />
      </SettingsList>
      <p style={{ fontSize: 12, color: t.text3, margin: '12px 0 0' }}>
        Inbound connections are set up by EKOM for now. Contact us to change one.
      </p>
    </>
  );
}
